import * as Sentry from "@sentry/react";

import type {
  CreateFramePayloadParsed,
  UpdateFramePayloadParsed,
  UpdateFrameVariantParsed,
} from "@/frames/schemas";
import type {
  ChannelMedium,
  EditorType,
  Frame,
  FrameApprovalStatus,
  FrameMetrics,
  FramePreview,
  FramePreviewManifest,
  FrameTags,
  FrameVariant,
  FrameVariantConfig,
  GetFrameVariantVersionsResponse,
} from "@/types/dragon-tiles";

export interface GatewayFrameMetrics {
  open_click_5d_rpm: number;
  currency_code: string;
}

function fromGatewayFrameMetrics(
  metrics: GatewayFrameMetrics | null | undefined,
): FrameMetrics | null {
  if (!metrics) return null;
  return {
    rpm: metrics.open_click_5d_rpm,
    currencyCode: metrics.currency_code,
  };
}

export interface GatewayFrameVariant {
  id: string;
  rj_id?: string;
  frame_id: string;
  rj_frame_id?: string;
  medium: ChannelMedium;
  content_text: string;
  content_url: string;
  content_json: string | FrameVariant["content_json"];
  content_plaintext?: string | null;
  created_by: string;
  editor_type: EditorType;
  create_date: string | null;
  modify_date: string | null;
  deleted_date: string | null;
  deleted_at: string | null;
  config: FrameVariantConfig | null;
  modified_by_email: string;
  modified_by_name: string;
  metrics?: GatewayFrameMetrics | null;
}

export interface GatewayFrame {
  id: string;
  rj_id?: string;
  site_id: number;
  rj_site_id?: string;
  inner_tile_id: string;
  rj_inner_tile_id?: string;
  slug: string;
  name: string;
  is_active: boolean | null;
  approval_status: FrameApprovalStatus;
  metadata: FrameTags | null;
  create_date: string | null;
  modify_date: string | null;
  deleted_date: string | null;
  frame_variants: GatewayFrameVariant[];
  modified_by: string;
  modified_by_email: string;
  modified_by_name: string;
  metrics?: GatewayFrameMetrics | null;
  blueprint?: { id: string; slug: string } | null;
}

export interface GatewayFrameVariantListing {
  id: string;
  rj_id?: string;
  frame_id: string;
  rj_frame_id?: string;
  medium: ChannelMedium;
  content_url: string;
  editor_type: EditorType;
  config: FrameVariantConfig | null;
  create_date: string | null;
  modify_date: string | null;
  deleted_at: string | null;
  modified_by_email: string;
  modified_by_name: string;
  metrics?: GatewayFrameMetrics | null;
}

export interface GatewayFrameListing {
  id: string;
  rj_id?: string;
  site_id: number;
  rj_site_id?: string;
  inner_tile_id: string;
  rj_inner_tile_id?: string;
  slug: string;
  name: string;
  is_active: boolean | null;
  approval_status: FrameApprovalStatus;
  metadata: FrameTags | null;
  create_date: string | null;
  modify_date: string | null;
  deleted_date: string | null;
  frame_variants: GatewayFrameVariantListing[];
  modified_by_email: string;
  modified_by_name: string;
  metrics?: GatewayFrameMetrics | null;
}

export interface GatewayRetrieveAllFramesResponse {
  frames: GatewayFrameListing[];
  total: number;
  limit: number;
  offset: number;
}

export interface GatewayFramePreviewVariantListing {
  id: string;
  frame_id: string;
  medium: ChannelMedium;
  editor_type: EditorType;
  create_date: string | null;
  modify_date: string | null;
  config: FrameVariantConfig | null;
  manifest?: unknown;
}

export interface GatewayFramePreviewListing {
  id: string;
  site_id: string;
  inner_tile_id: string;
  slug: string;
  name: string;
  is_active: boolean | null;
  approval_status: FrameApprovalStatus;
  metadata: FrameTags | null;
  create_date: string | null;
  modify_date: string | null;
  deleted_date: string | null;
  frame_variants: GatewayFramePreviewVariantListing[];
  metrics?: GatewayFrameMetrics | null;
}

export interface GatewayRetrieveAllFramePreviewsResponse {
  frame_previews: GatewayFramePreviewListing[];
  total: number;
  limit: number;
  offset: number;
}

export interface GatewayFrameVariantVersion {
  frame_variant: GatewayFrameVariant;
  valid_from: string | null;
  valid_until: string | null;
  modified_by: string;
  modified_by_email: string;
  modified_by_name: string;
}

export interface GatewayFrameVariantVersionsResponse {
  versions: GatewayFrameVariantVersion[];
}

// content_json rides the wire as a JSON string (connect-api's proto field is a
// string emitted verbatim); the editor and the rest of connect-ui expect the
// parsed ProjectData object. Tolerate an already-parsed object too (test fixtures).
function parseContentJson(
  value: string | FrameVariant["content_json"],
): FrameVariant["content_json"] {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    // A malformed/truncated string from the gateway must not throw — that would
    // break the whole frame (or page) load. Degrade to a blank canvas and
    // report so the bad payload stays visible.
    try {
      return JSON.parse(value) as FrameVariant["content_json"];
    } catch (error) {
      Sentry.captureException(error, { extra: { contentJson: value } });
      return null;
    }
  }
  return value;
}

export function fromGatewayFrameVariant(
  variant: GatewayFrameVariant,
): FrameVariant {
  return {
    id: variant.rj_id || variant.id,
    frame: variant.rj_frame_id || variant.frame_id,
    email: "",
    medium: variant.medium,
    editor_type: variant.editor_type,
    content_text: variant.content_text || null,
    content_url: variant.content_url || null,
    content_json: parseContentJson(variant.content_json),
    content_plaintext: variant.content_plaintext ?? null,
    create_date: variant.create_date ?? "",
    modify_date: variant.modify_date ?? "",
    deleted_date: variant.deleted_date ?? variant.deleted_at ?? null,
    config: variant.config ?? {},
    metrics: fromGatewayFrameMetrics(variant.metrics),
  };
}

export function fromGatewayFrame(frame: GatewayFrame): Frame {
  return {
    id: frame.rj_id || frame.id,
    inner_tile: frame.rj_inner_tile_id || frame.inner_tile_id,
    name: frame.name,
    is_active: frame.is_active ?? false,
    status: "",
    approval_status: frame.approval_status,
    slug: frame.slug,
    frame_variants: (frame.frame_variants ?? []).map(fromGatewayFrameVariant),
    metadata: frame.metadata ?? undefined,
    create_date: frame.create_date ?? "",
    modify_date: frame.modify_date ?? "",
    deletedAt: frame.deleted_date ?? null,
    metrics: fromGatewayFrameMetrics(frame.metrics),
    blueprint: frame.blueprint ?? null,
  };
}

export function fromGatewayFrameVariantListing(
  variant: GatewayFrameVariantListing,
): FrameVariant {
  return {
    id: variant.rj_id || variant.id,
    frame: variant.rj_frame_id || variant.frame_id,
    email: "",
    medium: variant.medium,
    editor_type: variant.editor_type,
    content_text: null,
    content_url: variant.content_url || null,
    content_json: null,
    create_date: variant.create_date ?? "",
    modify_date: variant.modify_date ?? "",
    deleted_date: variant.deleted_at ?? null,
    config: variant.config ?? {},
    metrics: fromGatewayFrameMetrics(variant.metrics),
  };
}

export function fromGatewayFrameListing(frame: GatewayFrameListing): Frame {
  return {
    id: frame.rj_id || frame.id,
    inner_tile: frame.rj_inner_tile_id || frame.inner_tile_id,
    name: frame.name,
    is_active: frame.is_active ?? false,
    status: "",
    approval_status: frame.approval_status,
    slug: frame.slug,
    frame_variants: (frame.frame_variants ?? []).map(
      fromGatewayFrameVariantListing,
    ),
    metadata: frame.metadata ?? undefined,
    create_date: frame.create_date ?? "",
    modify_date: frame.modify_date ?? "",
    deletedAt: frame.deleted_date ?? null,
    metrics: fromGatewayFrameMetrics(frame.metrics),
  };
}

export function fromGatewayFramePreviewListing(
  frame: GatewayFramePreviewListing,
  parseManifest: (raw: unknown) => FramePreviewManifest | null,
): FramePreview {
  return {
    id: frame.id,
    inner_tile: frame.inner_tile_id,
    name: frame.name,
    is_active: frame.is_active ?? false,
    status: "",
    approval_status: frame.approval_status,
    slug: frame.slug,
    frame_variants: (frame.frame_variants ?? []).map((variant) => ({
      id: variant.id,
      frame: variant.frame_id,
      email: "",
      medium: variant.medium,
      editor_type: variant.editor_type,
      content_text: null,
      content_url: null,
      content_json: null,
      create_date: variant.create_date ?? "",
      modify_date: variant.modify_date ?? "",
      deleted_date: null,
      config: variant.config ?? {},
      manifest: parseManifest(variant.manifest),
    })),
    metadata: frame.metadata ?? undefined,
    create_date: frame.create_date ?? "",
    modify_date: frame.modify_date ?? "",
    deletedAt: frame.deleted_date ?? null,
    metrics: fromGatewayFrameMetrics(frame.metrics),
  };
}

export function fromGatewayFrameVariantVersions(
  data: GatewayFrameVariantVersionsResponse,
): GetFrameVariantVersionsResponse {
  return {
    versions: (data.versions ?? []).map((version) => ({
      object_id: version.frame_variant.rj_id || version.frame_variant.id,
      revision_id: version.valid_from ? Date.parse(version.valid_from) : 0,
      field_dict: {
        ...fromGatewayFrameVariant(version.frame_variant),
        modify_date:
          version.frame_variant.modify_date ?? version.valid_from ?? "",
      },
      user:
        version.modified_by_name ||
        version.modified_by_email ||
        version.modified_by,
    })),
  };
}

type GatewayFrameVariantWrite = {
  id?: string;
  rj_id?: string;
  medium: string;
  editor_type?: string;
  content_text?: string;
  content_url?: string;
  content_json?: string;
  content_plaintext?: string;
  config?: unknown;
};

type GatewayFrameWrite = {
  name?: string;
  is_active?: boolean;
  approval_status?: string;
  slug?: string;
  inner_tile_id?: string;
  rj_inner_tile_id?: string;
  metadata?: Record<string, unknown>;
  frame_variants?: GatewayFrameVariantWrite[];
};

export type GatewayDuplicateFrameRequest = {
  name?: string;
  inner_tile_id?: string;
  mediums?: string[];
  exclude_metadata?: boolean;
};

function toGatewayVariantWrite(
  variant: CreateFramePayloadParsed["frame_variants"][number] &
    Partial<Pick<UpdateFrameVariantParsed, "id">>,
): GatewayFrameVariantWrite {
  const write: GatewayFrameVariantWrite = { medium: variant.medium };
  if (variant.id) {
    write.id = variant.id;
    write.rj_id = variant.id;
  }
  if (variant.editor_type) write.editor_type = variant.editor_type;
  if (variant.content_text !== null) write.content_text = variant.content_text;
  if (variant.content_url !== null) write.content_url = variant.content_url;
  if (variant.content_json !== null && variant.content_json !== undefined) {
    write.content_json = JSON.stringify(variant.content_json);
  }
  if (variant.content_plaintext != null) {
    write.content_plaintext = variant.content_plaintext;
  }
  if (variant.config !== undefined) write.config = variant.config;
  return write;
}

export function toGatewayFrameCreate(
  payload: CreateFramePayloadParsed,
): GatewayFrameWrite {
  return {
    name: payload.name,
    is_active: payload.is_active,
    approval_status: "pending",
    inner_tile_id: payload.inner_tile,
    rj_inner_tile_id: payload.inner_tile,
    ...(payload.metadata !== undefined && { metadata: payload.metadata }),
    frame_variants: payload.frame_variants.map(toGatewayVariantWrite),
  };
}

const updateMaskByField: Record<string, string> = {
  name: "name",
  is_active: "is_active",
  approval_status: "approval_status",
  slug: "slug",
  inner_tile: "inner_tile_id",
  metadata: "metadata",
};

export function toGatewayFrameUpdate(payload: UpdateFramePayloadParsed): {
  frame: GatewayFrameWrite;
  updateMask: string;
} {
  const frame: GatewayFrameWrite = {};
  const maskPaths: string[] = [];

  if (payload.name !== undefined) frame.name = payload.name;
  if (payload.is_active !== undefined) frame.is_active = payload.is_active;
  if (payload.approval_status !== undefined) {
    frame.approval_status = payload.approval_status;
  }
  if (payload.slug !== undefined) frame.slug = payload.slug;
  if (payload.inner_tile !== undefined) {
    frame.inner_tile_id = payload.inner_tile;
  }
  if (payload.metadata !== undefined) frame.metadata = payload.metadata;
  if (payload.frame_variants !== undefined) {
    frame.frame_variants = payload.frame_variants.map(toGatewayVariantWrite);
  }

  for (const [field, maskPath] of Object.entries(updateMaskByField)) {
    if (payload[field as keyof UpdateFramePayloadParsed] !== undefined) {
      maskPaths.push(maskPath);
    }
  }

  return { frame, updateMask: maskPaths.join(",") };
}
